import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

/**
 * Fenêtre modale de l'admin — une seule implémentation pour tout le CRM.
 *
 * Deux raisons de ne plus recopier un `<div className="fixed inset-0">` dans
 * chaque page :
 *
 * 1. Montage dans <body> (portail). Un `position: fixed` se place par rapport
 *    au plus proche ancêtre TRANSFORMÉ (transform/filter/backdrop-filter), pas
 *    par rapport à l'écran. Rendue dans la page, une modale héritait du bloc
 *    conteneur du chrome qui l'entoure : elle se centrait sur la hauteur du
 *    contenu, donc « en bas de page ». Dans <body>, elle est toujours centrée
 *    sur l'écran, quelles que soient les animations de la page.
 * 2. Hauteur bornée par l'écran (max-h + défilement interne) : sur mobile ou en
 *    fenêtre basse, un formulaire long reste entièrement atteignable au lieu de
 *    déborder hors de l'écran.
 *
 * L'appelant ne fournit que le contenu ; largeur via `size` (ou `className`,
 * fusionné par tailwind-merge pour un `max-w-[…]` sur mesure).
 */

const SIZES = {
  xs: "max-w-xs",
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "5xl": "max-w-5xl",
  "7xl": "max-w-7xl",
};

// Verrou de défilement du fond, partagé : une modale ouverte au-dessus d'une
// autre (confirmation de suppression, par ex.) ne doit pas rendre le
// défilement à la fermeture de la plus haute seulement.
let lockCount = 0;
let savedBodyStyle = null;

function lockScroll() {
  if (lockCount++ > 0) return;
  const { body } = document;
  // Compense la disparition de l'ascenseur pour éviter un saut horizontal.
  const gap = window.innerWidth - document.documentElement.clientWidth;
  savedBodyStyle = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
  body.style.overflow = "hidden";
  if (gap > 0) body.style.paddingRight = `${gap}px`;
}

function unlockScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount > 0 || !savedBodyStyle) return;
  document.body.style.overflow = savedBodyStyle.overflow;
  document.body.style.paddingRight = savedBodyStyle.paddingRight;
  savedBodyStyle = null;
}

// Pile des modales ouvertes : Échap ne ferme que celle du dessus.
const stack = [];

export default function Modal({
  open = true,
  onClose,
  children,
  size = "md",
  className,
  overlayClassName,
  zClassName = "z-50",
  align = "center",
  padded = true,
  dismissible = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  testid,
  overlayTestid,
  labelledBy,
  label,
}) {
  const panelRef = useRef(null);
  const downOnBackdrop = useRef(false);
  const token = useRef({});

  const close = useCallback(() => { if (dismissible) onClose?.(); }, [dismissible, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const self = token.current;
    stack.push(self);
    lockScroll();
    const onKey = (e) => {
      if (e.key !== "Escape" || !closeOnEscape) return;
      if (stack[stack.length - 1] !== self) return; // seule la modale du dessus réagit
      e.stopPropagation();
      close();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const i = stack.indexOf(self);
      if (i !== -1) stack.splice(i, 1);
      unlockScroll();
    };
  }, [open, closeOnEscape, close]);

  // Le focus entre dans la modale à l'ouverture : sans ça la tabulation et les
  // lecteurs d'écran resteraient sur la page du dessous. Un champ `autoFocus`
  // dans le contenu garde la priorité (il a déjà pris le focus au montage).
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  // Fermeture au clic sur le fond : on exige que l'appui ET le relâchement
  // aient eu lieu hors du panneau, sinon une sélection de texte relâchée en
  // dehors fermerait la modale au milieu d'une saisie.
  const onBackdropMouseDown = (e) => { downOnBackdrop.current = e.target === e.currentTarget; };
  const onBackdropClick = (e) => {
    if (!closeOnOverlayClick || e.target !== e.currentTarget || !downOnBackdrop.current) return;
    downOnBackdrop.current = false;
    close();
  };

  return createPortal(
    <div
      data-testid={overlayTestid}
      className={cn("fixed inset-0 overflow-y-auto overscroll-contain", zClassName, overlayClassName)}
    >
      {/* Voile : `fixed` (et non `absolute`) pour rester collé à l'écran quand
          le conteneur défile sur une modale plus haute que la fenêtre. */}
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-[2px]" aria-hidden="true" />
      <div
        onMouseDown={onBackdropMouseDown}
        onClick={onBackdropClick}
        className={cn(
          "relative flex min-h-full justify-center p-4 sm:p-6",
          align === "top" ? "items-start pt-[8vh] sm:pt-[12vh]" : "items-center"
        )}
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={labelledBy ? undefined : label}
          aria-labelledby={labelledBy}
          tabIndex={-1}
          data-testid={testid}
          className={cn(
            "relative w-full bg-white rounded-2xl shadow-2xl outline-none",
            "max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3rem)] overflow-y-auto",
            padded && "p-5 sm:p-6",
            SIZES[size] || SIZES.md,
            className
          )}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
